import React from 'react';
import { Drawer, List, ListItemButton, ListItemIcon, ListItemText, Box, Tooltip, Divider, Typography, IconButton } from '@mui/material';
import { 
  LocalHospital as DoctorDeskIcon,
  EventNote as BookingIcon,
  FitnessCenter as ActivitiesIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import AppVersionLabel from './AppVersionLabel';

const drawerWidthExpanded = 220;
const drawerWidthCollapsed = 64;

/**
 * Simplified Navigation for External (AIIMS) Users
 * Only shows: Doctor Desk, Individual Booking, Activities
 * Collapsible sidebar - shows icons when collapsed, full labels when expanded
 */
const ExternalNavigation = ({ activeTab, onTabChange, onLogout, doctorName, expanded, onToggle }) => {
  const navItems = [
    { key: 'doctorDesk', Icon: DoctorDeskIcon, label: 'Doctor Desk' },
    { key: 'booking', Icon: BookingIcon, label: 'New Booking' },
    { key: 'activities', Icon: ActivitiesIcon, label: 'Activities' },
  ];

  const drawerWidth = expanded ? drawerWidthExpanded : drawerWidthCollapsed;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        transition: 'width 0.2s ease-in-out',
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          background: 'linear-gradient(180deg, #C7AE6E 0%, #9F8752 100%)',
          color: '#fff',
          borderRight: '1px solid rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s ease-in-out',
          overflowX: 'hidden',
        },
      }}
    >
      {/* Logo & Toggle Button */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: expanded ? 'space-between' : 'center',
          pt: 2, 
          pb: 1,
          px: expanded ? 2 : 1,
          minHeight: 64,
        }}
      >
        {expanded ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="img"
                src="/atmantan-logo-a.png"
                alt="Atmantan"
                sx={{ width: 36, height: 'auto', opacity: 0.95 }}
              />
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.2 }}>
                  Atmantan
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', opacity: 0.8 }}>
                  AIIMS Portal
                </Typography>
              </Box>
            </Box>
            <IconButton 
              onClick={onToggle} 
              size="small"
              sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff' } }}
            >
              <ChevronLeftIcon />
            </IconButton>
          </>
        ) : (
          <IconButton 
            onClick={onToggle} 
            size="small"
            sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff' } }}
          >
            <ChevronRightIcon />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 1 }} />
      
      {/* Navigation Items */}
      <Box sx={{ flexGrow: 1, pt: 1 }}>
        <List>
          {navItems.map(({ key, Icon, label }) => (
            <Tooltip key={key} title={!expanded ? label : ''} placement="right" arrow>
              <ListItemButton 
                selected={activeTab === key} 
                onClick={() => onTabChange(key)} 
                sx={{ 
                  py: 1.5, 
                  px: expanded ? 2 : 1.5,
                  justifyContent: expanded ? 'flex-start' : 'center',
                  borderRadius: 0, 
                  '&.Mui-selected': { 
                    backgroundColor: '#B49A5F',
                    '& .MuiListItemIcon-root': { color: '#fff' },
                    '& .MuiListItemText-primary': { fontWeight: 600 }
                  }, 
                  '&.Mui-selected:hover': { backgroundColor: '#A3894F' }, 
                  '&:hover': { background: 'rgba(255,255,255,0.08)' } 
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: expanded ? 36 : 'auto', 
                  color: activeTab === key ? '#fff' : 'rgba(255,255,255,0.85)'
                }}>
                  <Icon />
                </ListItemIcon>
                {expanded && (
                  <ListItemText 
                    primary={label} 
                    sx={{ 
                      '& .MuiListItemText-primary': {
                        fontSize: '0.9rem',
                        color: activeTab === key ? '#fff' : 'rgba(255,255,255,0.9)'
                      }
                    }} 
                  />
                )}
              </ListItemButton>
            </Tooltip>
          ))}
        </List>
      </Box>

      {/* Doctor Info & Logout */}
      <Box sx={{ pb: 2 }}>
        <AppVersionLabel expanded={expanded} />
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', mb: 1 }} />
        {expanded && doctorName && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', opacity: 0.7 }}>
              Logged in as
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {doctorName}
            </Typography>
          </Box>
        )}
        <Tooltip title={!expanded ? 'Logout' : ''} placement="right" arrow>
          <ListItemButton
            onClick={onLogout}
            sx={{
              py: 1.5,
              px: expanded ? 2 : 1.5,
              justifyContent: expanded ? 'flex-start' : 'center',
              color: 'rgba(255,255,255,0.9)',
              '&:hover': {
                background: 'rgba(255,255,255,0.08)',
                color: '#fff'
              }
            }}
          >
            <ListItemIcon sx={{ 
              minWidth: expanded ? 36 : 'auto',
              color: 'rgba(255,255,255,0.85)'
            }}>
              <LogoutIcon />
            </ListItemIcon>
            {expanded && (
              <ListItemText 
                primary="Logout"
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: '0.9rem'
                  }
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Drawer>
  );
};

export default ExternalNavigation;
